import React, { useImperativeHandle, forwardRef, useState } from 'react';
import { validateInit, validateForm } from '@assets/js/util/formValidation.js';
import { formAtt } from '@assets/js/util/commonUtil.js';
import CompFmtInput from '@components/form/fragment/comp-fmt-input.js';
import CompFmtSelectbox from './fragment/comp-fmt-selectbox';
import {ApiSave} from '@assets/js/api/commonApi.js';

const CompFormCompetition = forwardRef((props, ref) => {
    const [params, setParams] = useState({});
    
    /****************************
     * Define required variables
    *****************************/
    const rootId = 'modalSheet';
    const validateOpt = {
        scopeEle: document.querySelector('#' + rootId),
        targetIds: ['comptnNm', 'useYn', 'viewYn','strtDate', 'endDate'],
        params: params,
    }

    /*************************
     * Define react functions
    **************************/
    // Forward ref to allow parent components to call event functions.
    useImperativeHandle(ref, () => ({
        modalToggle(p_params) {
            handleToggle();
            setParams(p_params || {});
        },
    }));

    /**********************
     * Define functions
    ***********************/
    const handleToggle = () => {
        const targetEle = document.querySelector('#' + rootId);
        if (targetEle) {
            targetEle.style.display = targetEle.style.display === 'none' ? 'block' : 'none';
        }

        // Reset form inputs and validation state
        validateInit(validateOpt);

        Object.keys(formObj).forEach(key => {
            const ele = targetEle?.querySelector(`#${formObj[key].id}`);
            if (ele) { ele.value = ''; }
        });
    }
    const onSave = () => {
        // Validate form inputs before saving
        if (!validateForm(validateOpt)) { return; }

        // Fetch data from the server
        ApiSave('/sys/comptmng/save/info', params).then(res => {
            if (res && res.success) {
                // Close the modal after saving
                handleToggle();
                // Search for updated data
                props.onGridSearch();
            }
        });

    }
    const onValChange = (e) => {
        const { id, value } = e.target;

        if (id === 'strtDate' || id === 'endDate') {
            dateChange(id, value);
            return;
        }

        // Validate the specific input field
        validateForm(validateOpt, id);
    }
    const onInputChange = (e) => {
        const { id, value } = e.target;
        // Update the state with the new value only (no validation)
        setParams({ ...params, [id]: value });
    }

    const dateChange = (id, value) => {
        value = value.split('-').join('');

        // Check if the value is an 8-digit number
        if (/^\d{8}$/.test(value)) {
            // Format the date to YYYY-MM-DD
            const year = value.substring(0, 4);
            const month = value.substring(4, 6);
            const day = value.substring(6, 8);
            const formattedValue = `${year}-${month}-${day}`;
            // Check if the formatted value is a valid date
            if (isNaN(new Date(formattedValue))) {
                setParams({ ...params, [id]: '' });
            } else {
                setParams({ ...params, [id]: formattedValue });
            }
        } else {
            // If not an 8-digit number, clear the value
            setParams({ ...params, [id]: '' });
        }

        // endDate의 값이 strtDate보다 작다면 공백처리
        const startDate = params.strtDate;
        const endDate = params.endDate;
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            if (end < start) {
                alert('대회 종료일은 시작일보다 빠를 수 없습니다.');
                setParams({ ...params, [id]: '' });
            }
        }
    }

    /****************************
     * Define form config
    *****************************/
    const formObj = {
        comptnNm    : formAtt({id: 'comptnNm', label: '대회명', value: params['comptnNm'] || '', required: true, maxLength: 100}),
        // comptnType  : formAtt({id: 'comptnType', label: '대회타입', value: params['comptnType'] || '', required: true, maxLength: 50, classCol: 'col-6'}),
        // hostOrg     : formAtt({id: 'hostOrg', label: '주최기관', value: params['hostOrg'] || '', required: true, maxLength: 100, classCol: 'col-6'}),
        dvsnGrpCd   : formAtt({id: 'dvsnGrpCd', label: '부문그룹', value: params['dvsnGrpCd'] || '', maxLength: 10, classCol: 'col-4'}),
        wetclsGrpCd : formAtt({id: 'wetclsGrpCd', label: '체급그룹', value: params['wetclsGrpCd'] || '', maxLength: 10, classCol: 'col-4'}),
        supptsGrpCd : formAtt({id: 'supptsGrpCd', label: '서포터즈그룹', value: params['supptsGrpCd'] || '', maxLength: 10, classCol: 'col-4'}),
        useYn       : formAtt({id: 'useYn', label: '사용여부', value: params['useYn'] || '', required: true, maxLength: 10, classCol: 'col-4'}),
        viewYn      : formAtt({id: 'viewYn', label: '대회 노출여부', value: params['viewYn'] || '', required: true, maxLength: 10, classCol: 'col-4'}),
        strtDate    : formAtt({id: 'strtDate', label: '대회시작일', value: params['strtDate'] || '', required: true, maxLength: 10, classCol: 'col-4'}),
        endDate     : formAtt({id: 'endDate', label: '대회종료일', value: params['endDate'] || '', required: true, maxLength: 10, classCol: 'col-4'}),
    }

    return (
        <>
            <div 
                className="modal modal-sheet p-1 py-md-5"
                style={{ display: 'none', backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
                tabIndex={-1}
                role="dialog"
                id={rootId}
            >
                <div className="modal-dialog modal-lg">
                    <div className="modal-content rounded-4 shadow">
                        <div className="modal-header border-bottom-0">
                            <h1 className="modal-title fs-5">대회정보 등록/수정</h1>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={handleToggle}
                            />
                        </div>
                        <div className="modal-body py-0">
                            <div className="row g-5">
                                <div className="col-lg-12">
                                    <form className="needs-validation" noValidate="">
                                        <div className="row">
                                            <div className="p-3 rounded">
                                                <div className="row mb-2">
                                                    <CompFmtInput
                                                        attrs={formObj.comptnNm}
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                    />
                                                </div>
                                                <div className="row mb-2">
                                                    <CompFmtSelectbox 
                                                        attrs={formObj.dvsnGrpCd}
                                                        grpCd={"DVSNGRP"}
                                                        selectType={"S"}
                                                        onChange={(e) => {
                                                            const { id, value } = e.target;
                                                            setParams({ ...params, [id]: value });
                                                            validateForm(validateOpt, id);
                                                        }}
                                                    />
                                                    <CompFmtSelectbox 
                                                        attrs={formObj.wetclsGrpCd}
                                                        grpCd={"WETCLSGRP"}
                                                        selectType={"S"}
                                                        onChange={(e) => {
                                                            const { id, value } = e.target;
                                                            setParams({ ...params, [id]: value });
                                                            validateForm(validateOpt, id);
                                                        }}
                                                    />
                                                    <CompFmtSelectbox 
                                                        attrs={formObj.supptsGrpCd}
                                                        grpCd={"SUPPTSGRP"}
                                                        selectType={"S"}
                                                        onChange={(e) => {
                                                            const { id, value } = e.target;
                                                            setParams({ ...params, [id]: value });
                                                            validateForm(validateOpt, id);
                                                        }}
                                                    />
                                                    </div>
                                                <div className="row mb-2">
                                                    <CompFmtSelectbox 
                                                        attrs={formObj.useYn}
                                                        grpCd={"GRP001"}
                                                        selectType={"S"}
                                                        onChange={(e) => {
                                                            const { id, value } = e.target;
                                                            setParams({ ...params, [id]: value });
                                                            validateForm(validateOpt, id);
                                                        }}
                                                    />
                                                    <CompFmtSelectbox 
                                                        attrs={formObj.viewYn}
                                                        grpCd={"GRP001"}
                                                        selectType={"S"}
                                                        onChange={(e) => {
                                                            const { id, value } = e.target;
                                                            setParams({ ...params, [id]: value });
                                                            validateForm(validateOpt, id);
                                                        }}
                                                    />
                                                </div>
                                                <div className="row mb-2">
                                                    <CompFmtInput
                                                        attrs={formObj.strtDate}
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                    />
                                                    <CompFmtInput
                                                        attrs={formObj.endDate}
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer flex-column align-items-stretch w-100 gap-2 pb-3 border-top-0">
                            <div className="btn-group">
                                <button type="button" className="btn btn-lg btn-primary" onClick={onSave}>
                                    Save changes
                                </button>
                                <button type="button" className="btn btn-lg btn-secondary" data-bs-dismiss="modal" onClick={handleToggle}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </> 
    );
});

export default CompFormCompetition;